import { useEffect } from 'react';
import type { RefObject } from 'react';
import { MERMAID_CLASS, type RenderResult } from '@markroom/renderer';
import { useViewer } from '../store/context';

/**
 * Lazily render every ```mermaid``` block found inside `containerRef`. Each
 * diagram renders independently: an invalid one shows an inline error panel with
 * its source and never affects the others or the rest of the document. Re-runs
 * when the document or the theme changes (the original source is preserved on a
 * data attribute so re-themes work after the SVG has replaced the source).
 */
export function useMermaid(
  containerRef: RefObject<HTMLElement | null>,
  renderId: number | null,
  render: RenderResult | null,
) {
  const themePref = useViewer((s) => s.themePref);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || !renderId || !render) return;
    const blocks = Array.from(root.querySelectorAll<HTMLElement>(`.${MERMAID_CLASS}`));
    if (blocks.length === 0) return;

    let cancelled = false;

    const run = async () => {
      const mermaid = (await import('mermaid')).default;
      const dark = document.documentElement.classList.contains('dark');
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: 'strict',
        theme: dark ? 'dark' : 'default',
      });

      for (let i = 0; i < blocks.length; i++) {
        if (cancelled) return;
        const el = blocks[i];
        if (!el) continue;
        // Preserve the original source the first time we touch this block.
        if (el.dataset.mermaidSource === undefined) {
          el.dataset.mermaidSource = (el.textContent ?? '').trim();
        }
        const source = el.dataset.mermaidSource;
        try {
          const { svg } = await mermaid.render(`markroom-mermaid-${renderId}-${String(i)}`, source);
          if (cancelled) return;
          el.innerHTML = svg;
          el.classList.remove('markroom-mermaid-error');
        } catch (err) {
          if (cancelled) return;
          renderErrorPanel(el, source, err);
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [containerRef, renderId, render, themePref]);
}

function renderErrorPanel(el: HTMLElement, source: string, err: unknown): void {
  const message = err instanceof Error ? err.message : 'Invalid diagram';
  el.classList.add('markroom-mermaid-error');
  el.replaceChildren();

  const heading = document.createElement('div');
  heading.className = 'markroom-mermaid-error__title';
  heading.textContent = `Mermaid diagram error: ${message}`;

  const pre = document.createElement('pre');
  pre.className = 'markroom-mermaid-error__source';
  pre.textContent = source; // textContent → no injection

  el.append(heading, pre);
}
